import React, { useState, useEffect, useRef } from 'react';
import './header.scss';
import '../../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import '../../../helper.scss';
import { convertToUpperCase } from '../../Utils/ToUpperCase/to-upper-case.tsx';
import anytrakkLogo from '../../../assets/anytrakk.png';
import Heading from '../Heading/heading.tsx';
import ConfirmationPopup from '../ConfirmationModal/confirmation-modal.tsx'; 
import { updateNotificationTune } from './action.ts';
import { getRoleFeatures } from '../../Pages/Login/action.ts';
import {
  Box,
  Dialog,
  DialogContent,
  Button,
  List,
  ListItem,
  Typography,
  IconButton,
  Chip
} from '@mui/material';

import notifySound from '../../../assets/1.mp3';
import tune1 from '../../../assets/2.mp3';
import tune2 from '../../../assets/3.mp3';
import tune3 from '../../../assets/4.mp3';
import tune4 from '../../../assets/5.mp3';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000; 

const TUNE_OPTIONS = [
  { id: '1', name: 'Ringing', src: notifySound },
  { id: '2', name: 'Siren', src: tune1 },
  { id: '3', name: 'Alarm', src: tune2 },
  { id: '4', name: 'Beep', src: tune3 },
  { id: '5', name: 'Alert', src: tune4 },
];

const getAudioSrc = (src: any) => typeof src === 'object' && src.default ? src.default : src;

const Header = () => {
  const [isOpen, toggleOpen] = useState(false);
  const menuClass = `dropdown-menu${isOpen ? " show" : ""}`;
  
  const [isBellOpen, setBellOpen] = useState(false);
  const [notifications, setNotifications] = useState(() => {
    const savedNotifs = localStorage.getItem('user_notifications');
    if (savedNotifs) {
      const parsed = JSON.parse(savedNotifs);
      const cutoffTime = Date.now() - SEVEN_DAYS_MS;
      return parsed.filter((n: any) => n.id > cutoffTime); 
    }
    return [];
  });
  
  const [unreadCount, setUnreadCount] = useState(0); 
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showTuneList, setShowTuneList] = useState(false);
  const [savedTune, setSavedTune] = useState(notifySound);
  const [playingSrc, setPlayingSrc] = useState<any>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [tuneToSave, setTuneToSave] = useState<any>(null);
  const activeAudioRef = useRef<HTMLAudioElement | null>(null);
  const currentTuneRef = useRef(savedTune);
  const bellMenuClass = `dropdown-menu${isBellOpen ? " show" : ""}`;
  const storedUserDetails = localStorage.getItem("userdetails");
  const userDetails = storedUserDetails ? JSON.parse(storedUserDetails) : null;
  const userName = userDetails && userDetails.user && userDetails.user.name ? userDetails.user.name.replace(/^"(.*)"$/, '$1') : null;
  const rawRole = userDetails?.user?.role || userDetails?.role || "";
  const userRole = typeof rawRole === 'string' ? rawRole.replace(/^"(.*)"$/, '$1') : rawRole;
  const bellRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const storedFeatures = localStorage.getItem("companyDetails");
      
      if (storedFeatures) {
        const parsedFeatures = JSON.parse(storedFeatures);
        const tuneId = parsedFeatures?.notification_tune || parsedFeatures?.message?.company?.notification_tune;
        
        if (tuneId) {
          const matchedTune = TUNE_OPTIONS.find(tune => tune.id === String(tuneId));
          if (matchedTune) {
            setSavedTune(matchedTune.src);
          }
        }
      }
    } catch (error) {
      console.error("Failed to parse companyDetails from local storage", error);
    }
  }, []);

  useEffect(() => {
    currentTuneRef.current = savedTune;
  }, [savedTune]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
        setBellOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        toggleOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (notifications.length > 0) {
      localStorage.setItem('user_notifications', JSON.stringify(notifications));
    }
  }, [notifications]);

  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const cutoffTime = Date.now() - SEVEN_DAYS_MS;
      setNotifications(prev => prev.filter(n => n.id > cutoffTime));
    }, 60 * 60 * 1000); 

    return () => clearInterval(cleanupInterval);
  }, []);

  useEffect(() => {
    let lastEventTime = 0;

    const handleNewNotification = (event: any) => {
      const { message } = event.detail;
      
      setNotifications((prev: any) => [
        { id: Date.now(), text: message, time: new Date().toLocaleTimeString() },
        ...prev
      ]);

      setUnreadCount((prev) => prev + 1);
      setIsAnimating(true);

      const now = Date.now();
      
      if (now - lastEventTime > 500) {
        lastEventTime = now;
        
        const safeSrc = getAudioSrc(currentTuneRef.current);
        const audio = new Audio(safeSrc);
        
        audio.play().catch(error => console.error("Audio playback failed:", error));
        
        audio.addEventListener('ended', function playSecondTime() {
          audio.currentTime = 0; 
          audio.play().catch(error => console.error("Audio playback failed:", error));
          audio.removeEventListener('ended', playSecondTime);
        });
      }
    };

    window.addEventListener('geofence-alert', handleNewNotification);

    return () => window.removeEventListener('geofence-alert', handleNewNotification);
  }, []);

  const clearNotifications = () => {
    setNotifications([]); 
    setUnreadCount(0); 
    setBellOpen(false);
    setIsAnimating(false); 
    localStorage.removeItem('user_notifications');
  };

  const handleBellClick = () => {
    setBellOpen((prev) => !prev);
    setUnreadCount(0); 
    setIsAnimating(false); 
  };

  const playPreviewTune = (rawSrc: any) => {
    if (playingSrc === rawSrc && activeAudioRef.current) {
      activeAudioRef.current.pause();
      setPlayingSrc(null); 
      return; 
    }

    if (activeAudioRef.current) {
      activeAudioRef.current.pause();
      activeAudioRef.current.currentTime = 0;
    }
    
    const safeSrc = getAudioSrc(rawSrc);
    const audio = new Audio(safeSrc);
    
    activeAudioRef.current = audio;
    
    audio.play().catch(err => console.error("Preview failed:", err));
    
    setPlayingSrc(rawSrc); 
    
    audio.addEventListener('ended', () => {
      setPlayingSrc(null);
    });
  };

  const closeSettings = () => {
    setIsSettingsOpen(false);
    setShowTuneList(false);
    if (activeAudioRef.current) {
      activeAudioRef.current.pause();
    }
    setPlayingSrc(null); 
  };

  const handleSetTuneClick = (tune: any) => {
    setTuneToSave(tune);
    setIsConfirmModalOpen(true);
  };

  const confirmUpdateTune = () => {
    if (tuneToSave) {
      updateNotificationTune(
        tuneToSave.id, 
        (response: any) => {
          setSavedTune(tuneToSave.src);
          setIsConfirmModalOpen(false);
          setTuneToSave(null);
          getRoleFeatures();
        },
        (error: any) => {
          setIsConfirmModalOpen(false);
        }
      );
    }
  };

  return (
    <>
      <nav className="navbar fixed-top header-navbar d-flex flex-nowrap align-items-center justify-content-between w-100 px-2">
        <div className="header-left">
          <img
            src={anytrakkLogo}
            alt="anytrakk_logo"
            className="header-logo"
            style={{ maxWidth: '120px', objectFit: 'contain' }}
          />
        </div>

        <div className="header-right d-flex align-items-center justify-content-end" style={{ gap: '10px' }}>
          
          <div className="dropdown" ref={bellRef}>
            <button
              className="btn position-relative p-1 d-flex align-items-center justify-content-center"
              onClick={handleBellClick}
              style={{ border: 'none', background: 'transparent', color:'#ebebeb', fontSize: '1.4rem' }}
            >
              <i className={`fas fa-bell ${isAnimating ? 'bell-animating' : ''}`}></i>
              
              {unreadCount > 0 && (
                <span 
                  className="position-absolute bg-danger text-white rounded-circle" 
                  style={{ top: '0px', right: '0px', fontSize: '0.65rem', fontWeight: 'bold', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', transform: 'translate(25%, -10%)', border: '1px solid #fff' }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <ul className={bellMenuClass} style={{ minWidth: '300px', padding: '0', right: '-10px', left: 'auto', position: 'absolute' }}>
              <li className="dropdown-header d-flex justify-content-between align-items-center bg-light border-bottom p-2">
                <strong style={{ color: '#333' }}>Notifications</strong>
                {notifications.length > 0 && (
                  <button className="btn btn-sm btn-link text-danger p-0" onClick={clearNotifications}>
                    Clear All
                  </button>
                )}
              </li>
              
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {notifications.length === 0 ? (
                  <li className="p-3 text-center text-muted">No new notifications</li>
                ) : (
                  notifications.map((notif: any) => (
                    <li key={notif.id} className="dropdown-item border-bottom" style={{ whiteSpace: 'normal', wordBreak: 'break-word', padding: '10px 15px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#000' }}>{notif.text}</div>
                      <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                          {new Date(notif.id).toLocaleDateString()} at {notif.time}
                      </small>
                    </li>
                  ))
                )}
              </div>
            </ul>
          </div>

          <div className="dropdown" ref={profileRef}>
            <button
              className="btn dropdown-toggle d-flex align-items-center"
              onClick={() => toggleOpen(prev => !prev)}
              style={{ background: 'linear-gradient(1turn, rgb(211, 211, 211), rgb(255, 255, 255))', color: 'black', border: 'none', fontWeight: 'bold', height: '35px', padding: '0 10px', maxWidth: '175px' }}
            >
              <i className="fas fa-user-circle me-1"></i>
              <span className="d-inline-block text-truncate" style={{ maxWidth: '175px', fontSize: '0.9rem' }}>
                {userName}
              </span>
            </button>

            <ul className={menuClass} style={{ right: 0, left: 'auto' }}>
              <li><a className="dropdown-item" href="#profile">Profile</a></li>
              <li>
                <button 
                  className="dropdown-item" 
                  onClick={() => {
                    if (
                      userRole === 'client_company_admin' || 
                      userRole === 'vendor_company admin' || 
                      userRole === 'vendor_company_admin'
                    ) {
                      setIsSettingsOpen(true);
                    }
                    toggleOpen(false);
                  }}
                >
                  Settings
                </button>
              </li>
              <li>
                <a
                  className="dropdown-item"
                  href="/"
                  onClick={() => {
                    localStorage.clear();
                    sessionStorage.removeItem('trips_active_tab');
                  }}
                >
                  Logout
                </a>
              </li>
            </ul>
          </div>
          
        </div>
      </nav>

      {/* --- MUI Settings Dialog --- */}
      <Dialog 
        open={isSettingsOpen} 
        onClose={closeSettings} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ style: { borderRadius: '8px' } }}
      >
        <Box sx={{ marginLeft: { sm: '-180px' } }}>
          <Heading title="Settings" closeIcon="true" onClose={closeSettings} />
        </Box>
        
        <DialogContent>
          {!showTuneList ? (
            <Button 
              variant="outlined" 
              fullWidth 
              size="large"
              startIcon={<i className="fas fa-music"></i>}
              onClick={() => setShowTuneList(true)}
              sx={{ justifyContent: 'flex-start', py: 1.5, mt: 1 }}
            >
              Notification Tune settings
            </Button>
          ) : (
            <Box>
              <Box display="flex" alignItems="center" mb={2}>
                <IconButton 
                  onClick={() => setShowTuneList(false)} 
                  size="small" 
                  sx={{ border: '1px solid #ccc', mr: 2, borderRadius: '4px' }}
                >
                  <i className="fas fa-arrow-left" style={{ fontSize: '14px' }}></i>
                </IconButton>
                <Typography variant="subtitle1" fontWeight="bold">
                  Select Notification Tune
                </Typography>
              </Box>
              
              <List disablePadding>
                {TUNE_OPTIONS.map((tune) => {
                  const isSelected = savedTune === tune.src;
                  
                  return (
                    <ListItem 
                      key={tune.id} 
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: isSelected ? '#e3f2fd' : 'transparent',
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Typography variant="body1" sx={{ color: isSelected ? 'primary.main' : 'text.primary', fontWeight: isSelected ? 'bold' : 'normal' }}>
                          {tune.name}
                        </Typography>
                        {isSelected && (
                          <Chip 
                            label="Current" 
                            color="primary" 
                            size="small" 
                            sx={{ ml: 1, height: '22px', fontSize: '0.7rem' }} 
                          />
                        )}
                      </Box>
                      
                      <Box display="flex" gap={1}>
                        <Button 
                          variant="contained" 
                          className="custom-button"
                          style={{ background: 'linear-gradient(135deg, #000000 20%, #e63946 100%)' }}
                          startIcon={<i className={`fas ${playingSrc === tune.src ? 'fa-pause' : 'fa-play'}`}></i>}
                          onClick={() => playPreviewTune(tune.src)}
                          sx={{ textTransform: 'none', boxShadow: 'none' }}
                        >
                          {playingSrc === tune.src ? 'Pause' : 'Play'}
                        </Button>

                        {!isSelected && (
                          <Button 
                            variant="outlined"
                            size="small"
                            className="custom-cancel-button"
                            onClick={() => handleSetTuneClick(tune)}
                            sx={{ textTransform: 'none', boxShadow: 'none' }}
                          >
                            Set as Tune
                          </Button>
                        )}
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* --- Reusable Confirmation Dialog --- */}
      <ConfirmationPopup 
        open={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmUpdateTune}
        title="Confirm Tune Change"
        message={
          <Typography>
            Are you sure you want to set <strong>{tuneToSave?.name}</strong> as your new notification tune?
          </Typography>
        }
      />
    </>
  );
};

export default Header;